import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { format } from 'date-fns';
import { Observable, of, throwError } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { EMPTY } from 'rxjs/internal/observable/empty';
import {
  catchError,
  delay,
  mergeMap,
  retryWhen,
  shareReplay,
  timeout
} from 'rxjs/operators';
import { UserService } from 'src/app/services/user.service';
import { btnLoading } from '../core/Functions';
import { ErpInterceptor } from '../http-interceptors/erp-interceptor';
import { HelperService } from './helpers.service';


const getErrorMessage = (maxRetry: number) =>
  'Foram feitas ' + maxRetry + ' tentativas de conexão com o servidor sem sucesso';
const MaximoTentativas = 5;
const tempoEntreTentativas = 2000;


@Injectable({
  providedIn: 'root',
})
export class EntregaService {
  public tentativas = 0;
  public disableButton = new BehaviorSubject(false);


  constructor(
    private httpClient: HttpClient,
    private helper: HelperService,
    private userS: UserService
  ) {}

  listarPedidos(userId, $event?): Observable<any> {
    const url = this.userS.API_URL + 'entrega/getByEntregador';
    const payload = { entregador_id: userId };
    return this.httpClient.post(url, payload).pipe(btnLoading($event));
  }

  postEntregaStart(id, $event): Observable<any> {
    this.tentativas = 0;
    const delivery_at = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    const payload = {id, delivery_at};
    return this.httpClient
      .post(this.userS.API_URL + 'entrega/updateApp', payload)
      .pipe(
        timeout(15000),
        btnLoading($event),
        this.retryHttp(5, 4),
        catchError((error) => {
          this.tentativas = 0;
          this.disableButton.next(false);
          this.helper.toast(
            `Não foi possível conectar-se com o servidor, verifique sua internet e tente novamente`,
            'warning'
          );
          this.helper.dismissLoader();
          return EMPTY;
        }),
        shareReplay()
      );
  }

  postEntregaCancel(id, $event): Observable<any> {
    this.tentativas = 0;
    const delivery_at = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    const cancelado_at = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    const payload = {id, cancelado_at, delivery_at};
    return this.httpClient
      .post(this.userS.API_URL + 'entrega/updateApp', payload)
      .pipe(
        timeout(15000),
        btnLoading($event),
        this.retryHttp(5, 4),
        catchError((error) => {
          this.tentativas = 0;
          this.disableButton.next(false);
          this.helper.toast(
            `Não foi possível conectar-se com o servidor, verifique sua internet e tente novamente`,
            'warning'
          );
          this.helper.dismissLoader();
          return EMPTY;
        }),
        shareReplay()
      );
  }

  postEntregaFinish(id, obs, $event): Observable<any> {
    this.tentativas = 0;
    const delivered_at = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    const payload = {id, obs, delivered_at};
    return this.httpClient
      .post(this.userS.API_URL + 'entrega/updateApp', payload)
      .pipe(
        timeout(15000),
        this.retryHttp(5, 4),
        btnLoading($event),
        catchError((error) => {
          this.tentativas = 0;
          this.disableButton.next(false);
          this.helper.toast(
            `Não foi possível conectar-se com o servidor, verifique sua internet e tente novamente`,
            'warning'
          );
          this.helper.dismissLoader();
          return EMPTY;
        }),
        shareReplay());
  }

  postAssinatura( filename, path, bin, $event): Observable<any> {
    this.tentativas = 0;
    const url = this.userS.API_URL + 'file/post';
    const payload = {  filename, path, bin };
    return this.httpClient.post(url, payload).pipe(
      timeout(15000),
      this.retryHttpAssinatura(5, 4),
      btnLoading($event),
      catchError((error) => {
        this.disableButton.next(false);
        this.tentativas = 0;
        this.helper.dismissLoader();
        return EMPTY;
      }),
      shareReplay()
    );
  }
  sendMessage(to): Observable<any> {
    const date = format(new Date(), 'dd/MM/yyyy');
    const body = `Informamos que o seu pedido foi entregue em: ${date}.

Conte sempre com a gente!`;

    const url = this.userS.API_URL + 'notification/sendMessage';
    const payload = { to, body };
    // return
    return this.httpClient.post(url, payload);
  }

  getMap(pedidoEnd) {
    this.helper.showLoader();
    ErpInterceptor.useErpHeader = false;
    this.httpClient
      .get(
        'https://maps.googleapis.com/maps/api/geocode/json?address=' +
          pedidoEnd +
          '&key=AIzaSyDjPQmROd0S2vBsQG3IijFWUfnNO2lUoPk'
      )
      .subscribe((result: any) => {
        const lat = parseFloat(result.results[0].geometry.location.lat);
        const lng = parseFloat(result.results[0].geometry.location.lng);
        const geolocation = lat + ',' + lng;
        window.open(
          'geo:' + geolocation + '?q=' + pedidoEnd.replace(/\s/g, '+')
        );
        this.helper.dismissLoader();
      });
  }

  retryHttp(
    delayMs: number,
    maxRetry = MaximoTentativas,
    backoffms = tempoEntreTentativas
  ) {
    this.tentativas++;
    let retries = maxRetry;
    return (src: Observable<any>) =>
      src.pipe(
        retryWhen((errors: Observable<any>) =>
          errors.pipe(
            mergeMap((error) => {
              if (retries-- > 0) {
                this.helper.dismissLoader();
                this.helper.showLoader(
                  `Houve um erro, tentando novamente...(${this.tentativas})`
                );
                const backofftime = delayMs + (maxRetry - retries) * backoffms;
                this.tentativas++;
                return of(error).pipe(delay(backofftime));
              }
              return throwError(getErrorMessage(maxRetry));
            })
          )
        )
      );
  }
  retryHttpAssinatura(
    delayMs: number,
    maxRetry = MaximoTentativas,
    backoffms = tempoEntreTentativas
  ) {
    let retries = maxRetry;
    return (src: Observable<any>) =>
      src.pipe(
        retryWhen((errors: Observable<any>) =>
          errors.pipe(
            mergeMap((error) => {
              if (retries-- > 0) {
                const backofftime = delayMs + (maxRetry - retries) * backoffms;
                return of(error).pipe(delay(backofftime));
              }
              return throwError(getErrorMessage(maxRetry));
            })
          )
        )
      );
  }
}
